import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import API from '@/api/api';

export function useEstimateDetailsData(estimateId) {
  const [loading, setLoading] = useState(true);
  const [siteLoading, setSiteLoading] = useState(false);
  const [estimateInfo, setEstimateInfo] = useState(null);
  const [estimateSites, setEstimateSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [activeSiteKey, setActiveSiteKey] = useState(null);
  const [siteCycles, setSiteCycles] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [optionalDocuments, setOptionalDocuments] = useState([]);

  const fetchCycles = useCallback(async (siteId, estId, signal) => {
    try {
      const response = await API.get(`/estimates/${estId}/sites/${siteId}`, { signal });
      setSiteCycles(response.data || []);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        message.error('사업장 산정 회차 정보 조회에 실패했습니다.');
      }
    }
  }, []);

  const changeActiveSite = useCallback(async (siteId) => {
    if (!estimateSites.length) return;
    const controller = new AbortController();
    setSiteLoading(true);
    setActiveSiteKey(siteId);
    const foundSite = estimateSites.find(s => s.estimate_site_info_id === siteId);
    setActiveSite(foundSite);
    await fetchCycles(siteId, estimateId, controller.signal);
    setSiteLoading(false);
    return () => controller.abort();
  }, [estimateSites, estimateId, fetchCycles]);

  const fetchData = useCallback(async (signal) => {
    setLoading(true);
    try {
      const infoRes = await API.get(`/estimates/${estimateId}`, { signal });
      const estimateData = infoRes.data;
      if (!estimateData) {
        throw new Error('Estimate info not found');
      }

      const docsRes = await API.get(`/projects/documents`, {
        params: { project_id: estimateData.project_id },
        signal
      });
      const documentsData = docsRes.data || [];
      setRequiredDocuments(documentsData.filter(d => d.is_required && d.document_type.is_active));
      setOptionalDocuments(documentsData.filter(d => !d.is_required && d.document_type.is_active));
      setEstimateInfo({ ...estimateData, documents: documentsData });

      const sitesRes = await API.get(`/estimates/${estimateId}/sites`, { signal });
      const sitesData = sitesRes.data || [];
      setEstimateSites(sitesData);

      if (sitesData.length > 0) {
        const initialSite = sitesData[0];
        const initialSiteId = initialSite.estimate_site_info_id;

        setSiteLoading(true);
        setActiveSiteKey(initialSiteId);
        setActiveSite(initialSite);

        await fetchCycles(initialSiteId, estimateId, signal);
        setSiteLoading(false);
      }

    } catch (error) {
      if (error.name !== 'CanceledError') {
        message.error('상세 데이터 조회에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [estimateId, fetchCycles]);

  useEffect(() => {
    const controller = new AbortController();
    if (estimateId) {
      fetchData(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [estimateId, fetchData]);

  return {
    loading,
    siteLoading,
    estimateInfo,
    estimateSites,
    activeSite,
    activeSiteKey,
    siteCycles,
    requiredDocuments,
    optionalDocuments,
    changeActiveSite,
  };
}
